import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-grow px-side gap-10" }

import TopBar from "@/components/TopBar.vue";
import BaseLoader from "@/components/BaseLoader.vue";
import UpcomingBar from "@/components/UpcomingBar.vue";
import Available from "@/views/Available.vue";
import InProgress from "@/views/InProgress.vue";
import ComingUp from "@/views/ComingUp.vue";
import Unavailable from "@/views/Unavailable.vue";
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import pusherService from "@/services/PusherService";
import { useProfileStore } from "@/store/Profile";
import { useScheduleStore } from "@/store/Schedule";
import { useWindowSize } from "@/composables/windowSize";
import loggingService from "@/services/LoggingService";
import * as Sentry from "@sentry/vue";
import {
  DAILY_REFRESH_BACKSTOP_DELAY,
  DAILY_REFRESH_BACKSTOP_JITTER,
  getLocalDateKey,
  msSinceLocalMidnight,
} from "@/helpers/Time";
import ScanToUse from "@/components/Qr/ScanToUse.vue";
import { QR_ON_TABLETS_OPTIONS } from "@/constants/";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const profileStore = useProfileStore();
const scheduleStore = useScheduleStore();

const showUpcoming = ref(false);
const initialLoading = ref(true);
const retriesNo = ref(0);

// Chosen once per page load so each tablet spreads its own backstop across the jitter window.
const dailyRefreshBackstopAt = DAILY_REFRESH_BACKSTOP_DELAY + Math.floor(Math.random() * DAILY_REFRESH_BACKSTOP_JITTER);
const lastDailyRefreshDate = ref(getLocalDateKey(new Date()));

const { width, height } = useWindowSize();

const currentScreen = computed(() => {
  const screens = {
    available: Available,
    inprogress: InProgress,
    comingup: ComingUp,
    unavailable: Unavailable,
  };

  return screens[scheduleStore.getScreenMode];
});

const showScanToUse = computed(() => {
  if (profileStore.qrOnTablets === QR_ON_TABLETS_OPTIONS.none) return false;

  if (profileStore.qrOnTablets === QR_ON_TABLETS_OPTIONS.all) return true;

  if (profileStore.qrOnTablets === QR_ON_TABLETS_OPTIONS.available && currentScreen.value === Available) return true;

  return false;
});

const style = computed(() => {
  const mode = scheduleStore.getScreenMode;

  const baseStyle = "flex flex-col";

  if (!profileStore.isConnected) {
    return `${baseStyle} bg-disconnected`;
  }

  return `${baseStyle} bg-${mode}`;
});

const refreshAtMidnight = async () => {
  loggingService.info("Home.vue", "♻️ Fetching new data at midnight");

  const { error } = await scheduleStore.refreshTablet({ source: "home:midnight", force: true });

  if (error) {
    loggingService.error("Home.vue", "❗️ Failed to fetch new data at midnight");
    Sentry.captureException(error);

    // Reload to avoid showing stale or empty data to the user
    window.location.reload();
    return;
  }

  const reservations = scheduleStore.getReservations;

  if (!reservations || reservations.length === 0) {
    loggingService.info("Home.vue", "🤷🏼‍♂️ No reservations at midnight");
  }
};

// Day-rollover backstop. `App.vue` already reloads the page at local midnight (+ up to 60s of
// jitter) and that reload refetches on mount, so on a normal night this never runs — after a reload
// `lastDailyRefreshDate` is re-initialised to the new date and the guard below short-circuits. It
// exists only for a reload that never fired, e.g. a long-lived setTimeout dropped by the browser.
watchEffect(() => {
  const now = scheduleStore.getTime;
  const today = getLocalDateKey(now);

  if (today === lastDailyRefreshDate.value) return;
  if (msSinceLocalMidnight(now) < dailyRefreshBackstopAt) return;

  lastDailyRefreshDate.value = today;
  refreshAtMidnight();
});

onBeforeMount(async () => {
  initialLoading.value = true;
  await scheduleStore.refreshTablet({ source: "home:onBeforeMount" });
  initialLoading.value = false;

  pusherService.initialize();
  profileStore.initializeAudio();

  const { data } = await profileStore.getProfile();

  // not saving it to store, as it is not going to be used elsewhere
  if (data?.profile.pingInterval !== null && data?.profile.pingInterval !== undefined) {
    const pingIntervalSeconds = Number(data.profile.pingInterval);

    if (Number.isFinite(pingIntervalSeconds) && pingIntervalSeconds >= 1) {
      const interval = pingIntervalSeconds * 1000 + Math.random() * 10;
      const pingUrl = data.profile.pingUrl || "/api/ping";
      const ackUrl = data.profile.pingAckUrl || "api/ack_reload";
      profileStore.startPing(interval, pingUrl, ackUrl);
    } else {
      loggingService.warn(
        "Home.vue",
        `⏱️ Received invalid ping interval (${data.profile.pingInterval}); skipping ping loop`
      );
    }
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(_unref(style)),
    style: _normalizeStyle({ width: _unref(width) + 'px', height: _unref(height) + 'px' })
  }, [
    (initialLoading.value)
      ? (_openBlock(), _createBlock(BaseLoader, { key: 0 }))
      : _createCommentVNode("", true),
    _createVNode(TopBar, {
      "is-connected": _unref(profileStore).isConnected,
      onCloseUpcoming: _cache[0] || (_cache[0] = ($event: any) => (showUpcoming.value = false))
    }, null, 8, ["is-connected"]),
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(currentScreen)), {
        class: "flex-grow w-full overflow-auto",
        onCloseUpcoming: _cache[1] || (_cache[1] = ($event: any) => (showUpcoming.value = false))
      })),
      (_unref(showScanToUse))
        ? (_openBlock(), _createBlock(ScanToUse, { key: 0 }))
        : _createCommentVNode("", true)
    ]),
    _createVNode(UpcomingBar, {
      "show-upcoming": showUpcoming.value,
      onSeeUpcoming: _cache[2] || (_cache[2] = ($event: any) => (showUpcoming.value = true)),
      onCloseUpcoming: _cache[3] || (_cache[3] = ($event: any) => (showUpcoming.value = false))
    }, null, 8, ["show-upcoming"])
  ], 6))
}
}

})